import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ProjectFailedToLoad } from "@/components/projects/ProjectErrors";

import { useListSecrets, type PlainSecret } from "@/queries/secret-queries";
import { useCallback, useMemo, useState, type FC } from "react";
import { useParams } from "react-router-dom";
import { NewSecretModal } from "@/components/secrets/NewSecretModal";
import { SecretsCard } from "@/components/secrets/SecretsCard";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import { useProject } from "@/queries/project-queries";
import { useRunnerCapabilities } from "@/hooks/use-runner-capabilities";
import { RunnerCapability } from "gitpod-next-api/gitpod/v1/runner_pb";
export const ProjectSecretsPage: FC = () => {
    const { projectId } = useParams();
    const { data: secrets, isPending, error } = useListSecrets(projectId);
    const { data: project } = useProject(projectId);
    const { hasCapability } = useRunnerCapabilities(projectId ?? "");

    const enableSecretContainerRegistry = useMemo(() => {
        const isLocalRunner =
            project?.environmentClass?.environmentClass.case === "localRunner" &&
            project?.environmentClass?.environmentClass.value === true;
        return hasCapability(RunnerCapability.SECRET_CONTAINER_REGISTRY) || isLocalRunner;
    }, [hasCapability, project?.environmentClass?.environmentClass]);

    const [showAddSecretModal, setShowAddSecretModal] = useState(false);

    const onNewSecret = useCallback(() => {
        setShowAddSecretModal(true);
    }, [setShowAddSecretModal]);

    const scope = useMemo(() => new SecretScope({ scope: { case: "projectId", value: projectId ?? "" } }), [projectId]);

    const content = useMemo(() => {
        if (error || !projectId) {
            return <ProjectFailedToLoad />;
        }

        if (isPending) {
            return <SkeletonBlock ready={!isPending} failed={!!error} className="h-[160px] w-[800px]" />;
        }

        return <ProjectSecrets scope={scope} secrets={secrets?.secrets ?? []} onNewSecret={onNewSecret} />;
    }, [error, isPending, projectId, secrets, scope, onNewSecret]);

    return (
        <div data-testid="project-details-secrets-page">
            {content}
            {showAddSecretModal && projectId && (
                <NewSecretModal
                    enableSecretContainerRegistry={enableSecretContainerRegistry}
                    scope={scope}
                    onClose={() => setShowAddSecretModal(false)}
                />
            )}
        </div>
    );
};

const ProjectSecrets: FC<{ scope: SecretScope; secrets: PlainSecret[]; onNewSecret: () => void }> = ({
    scope,
    secrets,
    onNewSecret,
}) => {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex justify-between">
                <div className="flex flex-col">
                    <Heading2 className="text-base font-bold text-content-primary">Secrets</Heading2>
                </div>
            </div>

            <SecretsCard scope={scope} secrets={secrets} onNewSecret={onNewSecret} />
        </div>
    );
};
